import { useCallback, useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { useEscapeEbene } from "@kundeninfo/ui";
import type { AblageZiel } from "./TicketZeitleiste.js";

/**
 * ÜBERGANG T-2 — Ziehen von Karten, minimal (Spec F44, Ruling zu Builder-Frage 2): Griff ⠿ wie
 * Positionseditor/`useZiehOrdnung`, Zielzone über `data-ablage` getönt (`tb__ziel--aktiv`, ein
 * freier Tag zusätzlich als Warnung `tb__ziel--frei`, 119), Textvorschau, Rand-Rollen, Escape
 * bricht ab. Bewusst EINE Datei: me-design Paket 51 bringt die Ziehhilfen und ersetzt sie dann.
 * Spalten werden in Schritt 1 nicht gezogen. Ein Zug beginnt erst ab ZIEH_SCHWELLE_PX Abstand
 * zum Aufsetzen. Nur Zeigerereignisse (kein HTML5-Ziehen): Maus,
 * Stift und Finger ziehen am selben Griff; am Telefon nur dort, sonst blockierte das Rollen.
 */

/** Die Daten-Attribute einer Zielzone. `warnung` (Wochenende/Feiertag) tönt die Zone als
 *  Warnung und steht in der Vorschau — abgelegt wird trotzdem (119). */
export function zielAttribute(z: AblageZiel, warnung = ""): Record<string, string> {
  const text = warnung === "" ? z.text : `${z.text} · ${warnung}`;
  return { "data-ablage": "1", "data-ablage-spalte": z.columnId, "data-ablage-ziel": z.ziel,
           "data-ablage-datum": z.datum ?? "", "data-ablage-text": text,
           ...(warnung === "" ? {} : { "data-ablage-frei": "1" }) };
}

export function zielAus(el: Element | null): AblageZiel | null {
  const host = el?.closest<HTMLElement>("[data-ablage]") ?? null;
  if (host === null) return null;
  const d = host.dataset;
  return { columnId: d.ablageSpalte!, ziel: d.ablageZiel as AblageZiel["ziel"],
           datum: d.ablageDatum === undefined || d.ablageDatum === "" ? null : d.ablageDatum,
           text: d.ablageText ?? "" };
}

const RAND = 40;
const SCHRITT = 24;
const TAKT_MS = 50;

/** Rollt jedes Feld, in dessen Randstreifen der Zeiger steht; true = eins hat gerollt. Mehrere
 *  Felder, weil das Board (me-board) waagerecht und jede Spalte (me-boardspalte) senkrecht für
 *  sich rollt. */
function randRollen(felder: readonly HTMLElement[], x: number, y: number): boolean {
  let gerollt = false;
  for (const feld of felder) {
    if (typeof feld.scrollBy !== "function") continue;
    const r = feld.getBoundingClientRect();
    const dx = x < r.left + RAND ? -SCHRITT : x > r.right - RAND ? SCHRITT : 0;
    const dy = y < r.top + RAND ? -SCHRITT : y > r.bottom - RAND ? SCHRITT : 0;
    if (dx === 0 && dy === 0) continue;
    feld.scrollBy({ left: dx, top: dy });
    gerollt = true;
  }
  return gerollt;
}

function gleich(a: AblageZiel | null, b: AblageZiel | null): boolean {
  return a === b || (a !== null && b !== null && a.columnId === b.columnId && a.ziel === b.ziel
                     && a.datum === b.datum && a.text === b.text);
}

export type KartenZug = { ticketId: string; ziel: AblageZiel | null };

/** Ab so vielen Pixeln Abstand zum Aufsetzen ist es ein Zug; darunter ein Wackler ohne Folgen. */
export const ZIEH_SCHWELLE_PX = 5;

export function useKartenZiehen(p: {
  /** Die Rollfelder unter dem Zeiger (x, y) für das Rand-Rollen. */
  rollfelder: (x: number, y: number) => readonly HTMLElement[];
  onAblage: (ticketId: string, ziel: AblageZiel) => void;
}): { zug: KartenZug | null; griff: (ticketId: string) => { onPointerDown: (e: ReactPointerEvent) => void } } {
  // `gedrueckt`: der Zeiger sitzt auf einem Griff (Horcher hängen). `zug`: erst ab der Schwelle —
  // vorher kein Ziel, keine Tönung, keine Vorschau, und Loslassen tut nichts.
  const [gedrueckt, setGedrueckt] = useState<string | null>(null);
  const [zug, setZug] = useState<KartenZug | null>(null);
  const zugRef = useRef(zug);
  zugRef.current = zug;
  const ablageRef = useRef(p.onAblage);
  ablageRef.current = p.onAblage;
  const rollfelderRef = useRef(p.rollfelder);
  rollfelderRef.current = p.rollfelder;
  const zeiger = useRef<number | null>(null);
  const start = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const markiert = useRef<HTMLElement | null>(null);
  const lage = useRef<{ x: number; y: number } | null>(null);

  const markieren = useCallback((el: HTMLElement | null) => {
    if (markiert.current === el) return;
    markiert.current?.classList.remove("tb__ziel--aktiv", "tb__ziel--frei");
    markiert.current = el;
    el?.classList.add("tb__ziel--aktiv");
    if (el?.dataset.ablageFrei === "1") el.classList.add("tb__ziel--frei");
  }, []);
  const beenden = useCallback(() => {
    markieren(null);
    zeiger.current = null;
    lage.current = null;
    setGedrueckt(null);
    setZug(null);
  }, [markieren]);

  // Escape am Stapel der Kundeninfo (wie `useZiehOrdnung`): bricht nur den Zug ab, nicht die
  // Schicht darunter. Der Fenster-Horcher unten fängt zusätzlich Escape ab, das nicht übers
  // Dokument läuft; `beenden` ist doppelt aufgerufen harmlos.
  useEscapeEbene(zug !== null, beenden);

  useEffect(() => {
    if (gedrueckt === null) return undefined;
    const ticketId = gedrueckt;
    const treffen = (x: number, y: number) => {
      const el = typeof document.elementFromPoint === "function" ? document.elementFromPoint(x, y) : null;
      const host = el?.closest<HTMLElement>("[data-ablage]") ?? null;
      markieren(host);
      const ziel = zielAus(host);
      setZug((z) => (z !== null && gleich(z.ziel, ziel) ? z : { ticketId, ziel }));
    };
    const bewegen = (e: PointerEvent) => {
      if (e.pointerId !== zeiger.current) return;
      if (lage.current === null
          && Math.hypot(e.clientX - start.current.x, e.clientY - start.current.y) < ZIEH_SCHWELLE_PX) return;
      lage.current = { x: e.clientX, y: e.clientY };
      treffen(e.clientX, e.clientY);
    };
    // Rand-Rollen nur im Takt (nicht zusätzlich je Bewegung): Steht der Zeiger im Randstreifen,
    // rollt das Feld gleichmäßig, auch im Stillstand, und das Ziel unter ihm wird neu bestimmt.
    const takt = setInterval(() => {
      const l = lage.current;
      if (l !== null && randRollen(rollfelderRef.current(l.x, l.y), l.x, l.y)) treffen(l.x, l.y);
    }, TAKT_MS);
    const loslassen = (e: PointerEvent) => {
      if (e.pointerId !== zeiger.current) return;
      const z = zugRef.current;
      const bewegt = lage.current !== null;
      beenden();
      // Nach einem echten Zug kein Klick hinterher: Er öffnete sonst das Ticket, über dem
      // losgelassen wurde.
      if (bewegt) {
        const schlucken = (k: MouseEvent) => { k.stopPropagation(); k.preventDefault(); };
        window.addEventListener("click", schlucken, { capture: true, once: true });
        setTimeout(() => window.removeEventListener("click", schlucken, { capture: true }), 0);
      }
      if (bewegt && z !== null && z.ziel !== null) ablageRef.current(z.ticketId, z.ziel);
    };
    const abbruch = (e: PointerEvent) => { if (e.pointerId === zeiger.current) beenden(); };
    const taste = (e: KeyboardEvent) => { if (e.key === "Escape") beenden(); };
    window.addEventListener("pointermove", bewegen);
    window.addEventListener("pointerup", loslassen);
    window.addEventListener("pointercancel", abbruch);
    window.addEventListener("keydown", taste);
    return () => {
      clearInterval(takt);
      window.removeEventListener("pointermove", bewegen);
      window.removeEventListener("pointerup", loslassen);
      window.removeEventListener("pointercancel", abbruch);
      window.removeEventListener("keydown", taste);
    };
  }, [gedrueckt, beenden, markieren]);

  // Verschwindet der Hook mitten im Zug (Board geschlossen), bleibt keine Tönung stehen.
  useEffect(() => () => { markiert.current?.classList.remove("tb__ziel--aktiv", "tb__ziel--frei"); }, []);

  const griff = (ticketId: string) => ({
    onPointerDown: (e: ReactPointerEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      // Einfangen wie in `useZiehOrdnung`: Verbesserung, keine Voraussetzung — jsdom kennt die
      // Methode nicht, ein Browser wirft bei einer verlorenen Kennung.
      try {
        const el = e.currentTarget as Element;
        if (typeof el.setPointerCapture === "function") el.setPointerCapture(e.pointerId);
      } catch { /* der Zug läuft auch ohne Einfangen */ }
      zeiger.current = e.pointerId;
      start.current = { x: e.clientX, y: e.clientY };
      lage.current = null;
      setGedrueckt(ticketId);
    },
  });
  return { zug, griff };
}
